/**
 * useOrbDocCoreConceptsTraitsSLDocEx2StatusLogic
 *
 * [shared]-entity logic hook.
 * Reads/writes the shared-entity store instead of a per-trait
 * reducer — see @almadar/ui/hooks/useSharedEntityStore.
 * Dispatch resolves transitions + effects fully client-side (no
 * server round trip).
 *
 * @generated from schema
 * @trait OrbDocCoreConceptsTraitsSLDocEx2Status
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSharedEntityStoreContext, useSharedEntitySnapshot, useEventBus, useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { emitCascadeEvent } from '@/hooks/useOrbitalBridge';
import type { EntityFieldWrite, EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import { mergeEntityFrame } from '@almadar/core';
import type { BusEvent } from '@almadar/core';
import type { EventResponse, OrbDocCoreConceptsTraitsSLThermostat, OrbDocCoreConceptsTraitsSLDocEx2StatusEventPayloadMap } from '@app/shared';

export interface OrbDocCoreConceptsTraitsSLDocEx2StatusConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
}

type OrbDocCoreConceptsTraitsSLDocEx2StatusSlot = 'main';
export interface OrbDocCoreConceptsTraitsSLDocEx2StatusLogicReturn {
  state: string;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: string | null;
  slotWriter: Partial<Record<OrbDocCoreConceptsTraitsSLDocEx2StatusSlot, string | null>>;
  loading: boolean;
  pendingEvent: string | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocCoreConceptsTraitsSLThermostat>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocCoreConceptsTraitsSLDocEx2StatusEventPayloadMap]: (payload?: OrbDocCoreConceptsTraitsSLDocEx2StatusEventPayloadMap[K]) => Promise<EventResponse> };
  config?: OrbDocCoreConceptsTraitsSLDocEx2StatusConfig;
}

export function useOrbDocCoreConceptsTraitsSLDocEx2StatusLogic(config?: Partial<OrbDocCoreConceptsTraitsSLDocEx2StatusConfig>): OrbDocCoreConceptsTraitsSLDocEx2StatusLogicReturn {
  const mergedConfig = config;

  // [shared] entity store + coalesced tick driver
  const _sharedStore = useSharedEntityStoreContext();
  const _sharedEntityId = 'OrbDocCoreConceptsTraitsSLThermostat';
  _sharedStore.seed(_sharedEntityId, { 'target': 21 });
  const _sharedSnapshot = useSharedEntitySnapshot(_sharedStore, _sharedEntityId);

  const fields = _sharedSnapshot as Partial<OrbDocCoreConceptsTraitsSLThermostat>;
  const data: Record<string, EntityRow[]> = { 'OrbDocCoreConceptsTraitsSLThermostat': [_sharedSnapshot] };

  // Local per-trait machine state — NOT the shared entity's fields,
  // which always live in the shared store (read fresh on every
  // dispatch, never cached here).
  const [machineState, setMachineState] = useState<string>('watching');
  const [lastPayload, setLastPayload] = useState<EventPayload | undefined>(undefined);
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [slotWriter, setSlotWriter] = useState<Partial<Record<OrbDocCoreConceptsTraitsSLDocEx2StatusSlot, string | null>>>({});
  const [error, setError] = useState<string | null>(null);
  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;

  const eventBus = useEventBus();

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['OrbDocCoreConceptsTraitsSLThermostat'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'watching' && event === 'INIT') {
      // watching → watching (on INIT)
      _newState = 'watching';
    } else if (fromState === 'watching' && event === 'SHOW') {
      // watching → watching (on SHOW)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocCoreConceptsTraitsSLDocEx2Status' }); emitCascadeEvent(eventBus, 'OrbDocCoreConceptsTraitsSL', 'OrbDocCoreConceptsTraitsSLDocEx2Status', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocCoreConceptsTraitsSL', trait: 'OrbDocCoreConceptsTraitsSLDocEx2Status' } }); })({ event: "SHOW" as const }));
      _newState = 'watching';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocCoreConceptsTraitsSLDocEx2StatusSlot[]; clear: OrbDocCoreConceptsTraitsSLDocEx2StatusSlot[] } => {
    switch (fromState) {
      case 'watching':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocCoreConceptsTraitsSLDocEx2StatusSlot[], clear: [] as OrbDocCoreConceptsTraitsSLDocEx2StatusSlot[] };
          case 'SHOW': return { write: ['main'] as OrbDocCoreConceptsTraitsSLDocEx2StatusSlot[], clear: [] as OrbDocCoreConceptsTraitsSLDocEx2StatusSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event against the shared entity, committing locally
   * first (the fields mirror IS the `[runtime]` row).
   * No server round trip — this trait declares `local`.
   */
  const dispatch = useCallback(async (event: string, payload?: EventPayload): Promise<EventResponse> => {
    const prevState = machineStateRef.current;
    const liveData: Record<string, EntityRow[]> = { 'OrbDocCoreConceptsTraitsSLThermostat': [_sharedStore.getSnapshot(_sharedEntityId)] };
    const result = executeTransitionEffects(prevState, event, liveData, payload);
    if (result.newState === undefined) {
      const errorMsg = `No transition defined for ${event} from ${prevState}`;
      setError(errorMsg);
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const writes: EntityFieldWrite[] = Object.entries(result.fields)
      .filter(([, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value: value as FieldValue }));
    if (writes.length > 0) {
      _sharedStore.commit(_sharedEntityId, mergeEntityFrame(_sharedStore.getSnapshot(_sharedEntityId), writes));
    }
    for (const _emit of result.emits) _emit();
    machineStateRef.current = result.newState;
    setMachineState(result.newState);
    const _renderSlots = getRenderUiSlots(prevState, event);
    if (_renderSlots.write.length > 0 || _renderSlots.clear.length > 0) {
      setLastPayload(payload);
      setLastEvent(event);
      setSlotWriter((prev) => ({
        ...prev,
        ...Object.fromEntries(_renderSlots.write.map((s) => [s, event] as const)),
        ...Object.fromEntries(_renderSlots.clear.map((s) => [s, null] as const)),
      }));
    }
    setError(null);
    return { success: true, transitioned: true, states: { 'OrbDocCoreConceptsTraitsSLDocEx2Status': result.newState }, emittedEvents: [] } as EventResponse;
  }, [executeTransitionEffects, getRenderUiSlots, _sharedStore, _sharedEntityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocCoreConceptsTraitsSLDocEx2StatusEventPayloadMap]: (payload?: OrbDocCoreConceptsTraitsSLDocEx2StatusEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'SHOW': (payload?: OrbDocCoreConceptsTraitsSLDocEx2StatusEventPayloadMap['SHOW']) => dispatch('SHOW', payload),
    'MOVED': (payload?: OrbDocCoreConceptsTraitsSLDocEx2StatusEventPayloadMap['MOVED']) => dispatch('MOVED', payload),
  };

  useUIEvents(enqueueEvent, 'OrbDocCoreConceptsTraitsSL.OrbDocCoreConceptsTraitsSLDocEx2Status', ['INIT', 'SHOW'] as const, eventBus);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'TARGET_CHANGED' (from OrbDocCoreConceptsTraitsSLDocEx2Dial) → trigger 'SHOW'
    const unsub_target_changed_from_orbdoccoreconceptstraitssldocex2dial_to_show = eventBus.on('UI:OrbDocCoreConceptsTraitsSL.OrbDocCoreConceptsTraitsSLDocEx2Dial.TARGET_CHANGED', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'OrbDocCoreConceptsTraitsSL.OrbDocCoreConceptsTraitsSLDocEx2Status', 'SHOW')) return;
      enqueueEvent('SHOW', event.payload);
    });
    return () => {
      if (typeof unsub_target_changed_from_orbdoccoreconceptstraitssldocex2dial_to_show === 'function') unsub_target_changed_from_orbdoccoreconceptstraitssldocex2dial_to_show();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state: machineState,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading: false,
    pendingEvent: null,
    pendingFrom: null,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
